import type { GameContext } from '../core/context';
import { FIXED_DT } from '../core/loop';
import { angleDelta, clamp, type V2 } from '../core/math';
import type { Rng } from '../core/rng';
import type { System } from '../core/system';
import type { Body } from '../physics/body';
import { Layer } from '../physics/types';
import type { PlayerService } from '../player/playerSystem';
import type { HeatService } from '../heat/types';
import type { Lane, RoadNet } from '../traffic/roadNet';
import type { TrafficService } from '../traffic/types';
import { ViewTester } from '../traffic/visibility';
import type { Vehicle } from '../vehicles/types';
import type { VehicleService } from '../vehicles/vehicleSystem';
import type { CityLayout, PointOfInterest } from '../world/types';
import { CopView, type CopPose } from './copView';
import {
  DODGE_OFFSET,
  PathFollower,
  RAM_MAX_ANGLE,
  ROADBLOCK_MIN_AHEAD,
  ROADBLOCK_MISS_S,
  TACTIC_RERANK,
  driveToward,
  nearestNode,
  pickSearchGoal,
  planRecovery,
  roadblockSite,
  sidesForRanks,
  tacticAim,
  tacticForRank,
  vehicleAhead,
  type PursuitTactic,
  type StuckMemory,
} from './policeDriver';
import { deadReckon, type SuspectEstimate } from './sighting';
import type { PoliceInfo, PoliceObserver, PoliceRole, PoliceService, PoliceUnitInfo, RemotePoliceSource, RemotePoliceUnit } from './types';

/**
 * Police (order 250, decision band). Units are ordinary vehicles: patrols are driven by the traffic
 * module (they obey lights, pull up at junctions), everything else by the police driver here.
 *
 * Per level (see docs/POLICE.md for numbers):
 *   0  two patrols drive about; they notice reckless driving, reported stolen cars, incidents.
 *   1  units go to the last known position and investigate; they arrest if they find you.
 *   2  active pursuit: chase, follow, close in when you slow down.
 *   3  more units, interceptors aim ahead, one rams (PIT), a roadblock ahead on your route.
 *   4  everything: ramming, boxing in, two roadblocks, aggressive foot officers.
 * Spawns only happen off-screen, nobody teleports, and units can crash, be outrun and be wrecked.
 *
 * Online the units are the server's (server/police/force.ts): `setRemote(source)` stands the local
 * force down. Observers, units, the arrest meter and armed-response state then come from the source,
 * and the foot officers drawn here are the server's; arrests and police gunfire are decided there.
 */

// ------------------------------------------------------------------ tuning (see docs/POLICE.md)
// The level tables live in ./tuning (pure data, shared with the server-run units online) and are
// re-exported here for existing importers.
import {
  ARMED_AIM,
  ARMED_AIM_FIRST,
  ARMED_CARS,
  ARMED_CLOSE,
  ARMED_RANGE,
  ARMED_SHOT_COOLDOWN,
  ARMED_SHOT_DAMAGE,
  ARMED_SHOT_GAP,
  BUST_TIME_CAR,
  BUST_TIME_FOOT,
  CAR_CAP,
  CAR_SIGHT,
  DISMOUNT_DELAY,
  FIREARM_CRIMES,
  FOOT_CAP,
  FOOT_RUN,
  FOOT_SIGHT,
  OFFLINE_FOOT_STANDOFF,
  PURSUIT_SPEED,
  ROADBLOCK_CAP,
  ROADBLOCK_INTERVAL,
  ROADBLOCK_SIGHT,
  SPAWN_INTERVAL,
  SPAWN_MAX_R,
  SPAWN_MIN_BY_LEVEL,
  ARMED_AFTER_RAM_LEVEL,
  ARMED_FROM_LEVEL,
  CAR_SHOT_COOLDOWN,
  CAR_SHOT_DAMAGE,
  CAR_SHOT_RANGE,
  FIRE_AT_SUSPECT_LEVEL,
  GLASS_HIT_CHANCE,
  GLASS_HIT_DAMAGE,
  HARD_RAM_SPEED,
  MARKSMAN_CAR_DAMAGE,
  MARKSMAN_GLASS_DAMAGE,
  MARKSMAN_RANGE,
  SHOOT_CAR_BODY_LEVEL,
  STINGER_LEVEL,
  STINGER_PUNCTURES,
  STINGER_SPEED,
  TAKEDOWN_RANGE,
  TAKEDOWN_SLOWMO_S,
  VAN_CAP,
  VAN_CREW,
  VAN_SPEC,
  armedHitChance,
  carShotHitChance,
} from './tuning';
import { StingerView } from './stingerView';
import { POLICE_TAP_SPEED } from '../heat/heatModel';
export { ARMED_HIT_CHANCE } from './tuning';
export {
  ARMED_AIM,
  ARMED_AIM_FIRST,
  ARMED_CARS,
  ARMED_RANGE,
  ARMED_SHOT_COOLDOWN,
  ARMED_SHOT_DAMAGE,
  ARMED_SHOT_GAP,
  BUST_TIME_CAR,
  CAR_CAP,
  CAR_SIGHT,
  DISMOUNT_DELAY,
  FOOT_CAP,
  FOOT_RUN,
  FOOT_SIGHT,
  PURSUIT_SPEED,
  ROADBLOCK_CAP,
  ROADBLOCK_SIGHT,
  SPAWN_INTERVAL,
  SPAWN_MIN_BY_LEVEL,
};
const SPAWN_MIN_R = 70;
const ROADBLOCK_LIFE_S = 55;
/** A unit `standBack` drove off stays out of the chase this long (s), so a mission's first chase starts clean. */
const STAND_BACK_S = 20;
const DESPAWN_R = 210;
/** A suspect wheel-spinning against the units (throttle held, no handbrake) is not surrendering: the meter crawls. */
export const BUST_STRUGGLE_RATE = 0.3;
/** A unit merely parked beside a stopped car starts the meter only after the car has sat still this long. */
const BUST_PULLED_OVER = 1.5;
/** Deceleration (m/s²) the police driver plans its approach to a suspect in a car with. */
const APPROACH_DECEL = 6;
/** Seconds of waiting with the crew out before the car calls its officers back. */
const CREW_WAIT_MAX = 12;
const RESPAWN_DELAY = 3.2;
/**
 * A collision with a unit is the player's hitPolice only when they were closing at least this many times
 * faster than the unit was closing on them (a head-on at 7 vs 6 m/s is nobody's crime; 20 vs 5 is a ram).
 */
export const UNIT_RAM_SHARE = 2;
/**
 * "Laid on the tarmac" (docs/POLICE.md §14). A contact and the damage it did are matched within
 * CONTACT_WINDOW (s); a disabled unit counts as a forced crash only if you haven't rammed or shot it for
 * FORCED_CLEAN_FOR seconds, most of its damage was its own crashes, and it saw you within FORCED_SAW_WITHIN.
 */
export const CONTACT_WINDOW = 0.3;
export const FORCED_CLEAN_FOR = 3;
export const FORCED_SAW_WITHIN = 4;
/**
 * A pursuing unit that loses this share of its health to its own crashes within KNOCKOUT_WINDOW seconds (one
 * hard crash: a wall at ~22 m/s, a head-on with traffic) is out of the chase however healthy it was: the
 * crew are shaken and the radiator's gone. Server: force.ts UNIT_KNOCKOUT.
 */
export const UNIT_KNOCKOUT = 0.25;
const KNOCKOUT_WINDOW = 0.5;
/** A unit laid on the tarmac is written off: at most this share of its health, so it smokes heavily (as online). */
export const WRITTEN_OFF = 0.18;
/** A car must be moving towards a foot officer at least this fast (m/s) for knocking him down to be the driver's hitPolice. */
export const OFFICER_HIT_INTO = 3;
const COP_ENTITY_BASE = 900000;
const HEALTH_MAX = 100;

type Tactic = PursuitTactic;

class CarUnit {
  id = 0;
  vehicle!: Vehicle;
  role: PoliceRole = 'patrol';
  tactic: Tactic = 'chase';
  side = 1;
  readonly path = new PathFollower();
  goal: V2 | null = null;
  roleSince = 0;
  lingerUntil = 0;
  stuckFor = 0;
  queueFor = 0;
  recoverUntil = 0;
  recoverSteer = 0;
  /** Where it got stuck last, how often in a row; dodge sideways / follow the road for a while after a recovery. */
  readonly stuck: StuckMemory = { at: null, time: -99, count: 0 };
  dodgeUntil = 0;
  dodgeSide = 1;
  routeUntil = 0;
  /** Crew aboard, and the full crew (2 in a car, VAN_CREW in the van). */
  crewInCar = 2;
  crew = 2;
  seesPlayer = false;
  lastSawAt = -99;
  /** Where the crew last SAW the suspect, and how they were moving then (dead reckoning once out of sight). */
  readonly sawPos: V2 = { x: 0, z: 0 };
  readonly sawVel: V2 = { x: 0, z: 0 };
  sawHeading = 0;
  hitReadyAt = 0;
  backoffUntil = 0;
  disabled = false;
  disabledAt = 0;
  /**
   * Whose damage is it ("laid on the tarmac", docs/POLICE.md §14): health lost to the player (a ram that was
   * theirs, a shot, a blast they caused) vs to the unit's own crashes, and when the player last hurt it.
   * `excusedAt`: a contact with the player's car that was NOT their fault (the unit rammed them).
   */
  lastHealth = Number.NaN;
  crashDamage = 0;
  playerDamage = 0;
  blameAt = -99;
  excusedAt = -99;
  playerHitAt = -99;
  /** Own-crash damage in the current burst (KNOCKOUT_WINDOW) and when it began; set once a hard crash knocked it out. */
  crashBurst = 0;
  crashBurstAt = -99;
  knockedOut = false;
  roadblockUntil = 0;
  /** Roadblock: seconds the suspect has been far off and heading away from it. */
  missFor = 0;
  /** Sent away by `standBack` (a mission's gentle start): not called back into the chase before this time. */
  standBackUntil = 0;
  /** Sim time the crew got out (0 = all aboard). */
  crewOutSince = 0;
  /** Armed response car (crew carry revolvers); the marksman's car carries a rifle (level 4). */
  armed = false;
  marksman = false;
  /** Passenger's aim wind-up at the suspect's car (s), next shot time, shots fired (docs/POLICE-AI-POLISH.md). */
  aim = 0;
  shotReadyAt = 0;
  shots = 0;
}

/** A spike strip across the road ahead of a level-4 roadblock. */
interface Stinger {
  id: number;
  a: V2;
  b: V2;
  until: number;
  hitAt: number;
}

class FootCop {
  id = 0;
  body!: Body;
  carId = 0;
  state: 'chase' | 'return' | 'search' | 'down' = 'chase';
  yaw = 0;
  phase = 0;
  speed = 0;
  lean = 0;
  fade = 1;
  downFor = 0;
  prevX = 0;
  prevZ = 0;
  seesPlayer = false;
  goal: V2 | null = null;
  /** Progress tracking: where he was at `checkAt`, and how many checks in a row he barely moved. */
  checkX = 0;
  checkZ = 0;
  checkAt = 0;
  stalls = 0;
  /** Sidestepping round an obstacle until this time (direction ±1). */
  skirtUntil = 0;
  skirtSide = 1;
  /** Sent back to the car: don't rejoin the chase until this time unless the suspect is right here. */
  recalledUntil = 0;
  /** Armed officer: aim wind-up (s), next shot time, shots fired, last shot (for the recoil pose). */
  armed = false;
  aim = 0;
  shotReadyAt = 0;
  shots = 0;
  firedAt = -99;
}

export function createPoliceSystem(): System {
  let ctx: GameContext;
  let rng: Rng;
  let layout: CityLayout;
  let vehicles: VehicleService;
  let traffic: TrafficService | undefined;
  let net: RoadNet | undefined;
  let view: ViewTester;
  let copView: CopView | null = null;
  const cars: CarUnit[] = [];
  const cops: FootCop[] = [];
  const observers: PoliceObserver[] = [];
  const seers = new Set<number>();
  let nextId = 1;
  const stats = { spawned: 0, spawnFailed: 0, rejectVisible: 0, rejectDistance: 0, rejectFilter: 0, rejectSlot: 0 };
  let spawnReadyAt = 0;
  let patrolReadyAt = 0;
  let roadblockReadyAt = 0;
  let rankedKey = '';
  let rankedAt = -99;
  /** Junctions a searching unit may head for: nodes on at least one real road (alley-only ones wedge a saloon). */
  let searchNodes: readonly { pos: V2 }[] = [];
  let bust = 0;
  /** Seconds the suspect has been (nearly) stopped; reset once they get going again. */
  let stillFor = 0;
  let outcome: 'none' | 'busted' | 'wasted' = 'none';
  let outcomeTimer = 0;
  let outcomePenalty = 0;
  let outcomeGate: ((kind: 'busted' | 'wasted') => boolean) | null = null;
  let health = HEALTH_MAX;
  let lastHurtAt = -99;
  let incident: { pos: V2; until: number } | null = null;
  /** The player used or showed a gun during this wanted episode. */
  let firearmsIncident = false;
  let armedAuthorised = false;
  let armedCleared = false;
  /** The player rammed a unit at HARD_RAM_SPEED or more this episode: the armed cars come a level early. */
  let hardRam = false;
  const stingers: Stinger[] = [];
  let nextStingerId = 1;
  let stingerView: StingerView | null = null;
  let aruReadyAt = 0;
  let armedShotReadyAt = 0;
  let incidentReadyAt = 0;
  const offs: (() => void)[] = [];
  const aim: V2 = { x: 0, z: 0 };
  const dodgeAim: V2 = { x: 0, z: 0 };
  const poses: CopPose[] = [];
  /** Online: the server's units (null = the local police simulate, offline). */
  let remote: RemotePoliceSource | null = null;
  let remoteHurtSeq = 0;

  const player = (): PlayerService | undefined => ctx.services.tryGet('player');
  const heat = (): HeatService | undefined => ctx.services.tryGet('heat');
  const level = (): number => heat()?.level ?? 0;

  function playerPos(): V2 {
    return player()?.pos ?? layout.playerSpawn.pos;
  }

  function target(): { pos: V2; vel: V2; heading: number; onFoot: boolean; vehicle: Vehicle | null; speed: number } {
    const p = player();
    const vel = p?.velocity ?? { x: 0, z: 0 };
    const veh = p && p.mode === 'driving' ? p.vehicle : null;
    return { pos: playerPos(), vel, heading: p?.heading ?? 0, onFoot: !veh, vehicle: veh, speed: Math.hypot(vel.x, vel.z) };
  }

  // ---------------------------------------------------------------- spawning

  function laneSlotOffscreen(filter: (lane: Lane, pos: V2) => boolean, minR = SPAWN_MIN_R, maxR = SPAWN_MAX_R): { lane: Lane; s: number; pos: V2 } | null {
    if (!net || !traffic) return null;
    const lanes = net.lanes;
    const p = playerPos();
    for (let tries = 0; tries < 60; tries++) {
      const lane = lanes[rng.int(0, lanes.length - 1)]!;
      if (lane.alley || lane.length < 14) continue;
      const s = rng.range(5, lane.length - 5);
      const pos = { x: lane.a.x + lane.dir.x * s, z: lane.a.z + lane.dir.z * s };
      const d = Math.hypot(pos.x - p.x, pos.z - p.z);
      if (d < minR || d > maxR) {
        stats.rejectDistance++;
        continue;
      }
      if (view.isVisible(pos.x, pos.z, 6)) {
        stats.rejectVisible++;
        continue;
      }
      if (!filter(lane, pos)) {
        stats.rejectFilter++;
        continue;
      }
      if (!traffic.slotFree(lane, s, 2.6)) {
        stats.rejectSlot++;
        continue;
      }
      return { lane, s, pos };
    }
    stats.spawnFailed++;
    return null;
  }

  function addCar(v: Vehicle, role: PoliceRole): CarUnit {
    const u = new CarUnit();
    u.id = nextId++;
    u.vehicle = v;
    u.role = role;
    u.roleSince = ctx.state.time;
    u.side = rng.chance(0.5) ? 1 : -1;
    v.tags.add('police-unit');
    v.data.police = { unitId: u.id };
    cars.push(u);
    return u;
  }

  function spawnPatrolAt(lane: Lane, s: number): CarUnit | null {
    if (!traffic) return null;
    const v = traffic.spawnOnLane(lane, s, 'police', { persistent: true });
    if (!v) return null;
    v.siren = false;
    return addCar(v, 'patrol');
  }

  function spawnPursuer(role: PoliceRole, spec = 'police'): CarUnit | null {
    const p = playerPos();
    const t = target();
    const ahead = level() >= 3 && rng.chance(0.6) && t.speed > 6;
    const lv = level();
    const slot = laneSlotOffscreen((lane, pos) => {
      const tx = p.x - pos.x;
      const tz = p.z - pos.z;
      const tl = Math.hypot(tx, tz) || 1;
      // Lanes heading towards the player; at high levels half the units come from in front.
      if ((lane.dir.x * tx + lane.dir.z * tz) / tl < -0.2) return false;
      if (ahead) return (pos.x - p.x) * t.vel.x + (pos.z - p.z) * t.vel.z > 0;
      return true;
    }, SPAWN_MIN_BY_LEVEL[lv]!, SPAWN_MAX_R - (lv >= 3 ? 25 : 0));
    if (!slot) return null;
    const heading = Math.atan2(slot.lane.dir.x, slot.lane.dir.z);
    const v = vehicles.spawn(spec, slot.pos, { heading, controller: 'ai', occupant: { kind: 'ped', pedId: null }, tags: ['police'] });
    const sp = slot.lane.speedLimit;
    v.body.setVelocity({ x: slot.lane.dir.x * sp, z: slot.lane.dir.z * sp });
    v.siren = true;
    stats.spawned++;
    const u = addCar(v, role);
    if (spec === VAN_SPEC) u.crew = u.crewInCar = VAN_CREW;
    return u;
  }

  // ---------------------------------------------------------------- firearms incidents

  function resetArmed(): void {
    firearmsIncident = false;
    armedAuthorised = false;
    armedCleared = false;
    hardRam = false;
  }

  /**
   * Escalation for gun crime: level 3 with a firearms incident authorises armed units (they arrive
   * on top of the normal force and draw on you); level 4 clears them to fire. Telegraphed by events
   * the weapons module presents (radio line, shouted warning, aim before every shot).
   */
  function updateArmedResponse(): void {
    const lv = level();
    if (lv === 0) {
      resetArmed();
      return;
    }
    // Looser ladder (docs/POLICE-AI-POLISH.md "Proposed escalation"): armed cars come with level 3, a level
    // earlier after a gun or a hard ram; they shoot at the car (tyres) at once and at a suspect on foot from
    // level 4, or from 3 in a firearms incident.
    const authorise = lv >= ARMED_FROM_LEVEL || (lv >= ARMED_AFTER_RAM_LEVEL && (firearmsIncident || hardRam));
    if (authorise && !armedAuthorised) {
      armedAuthorised = true;
      ctx.events.emit('armedResponse', { stage: 'authorised', level: lv });
    }
    const clear = lv >= FIRE_AT_SUSPECT_LEVEL || (lv >= FIRE_AT_SUSPECT_LEVEL - 1 && firearmsIncident);
    if (armedAuthorised && clear && !armedCleared) {
      armedCleared = true;
      ctx.events.emit('armedResponse', { stage: 'cleared', level: lv });
    }
    if (!armedAuthorised) return;
    const now = ctx.state.time;
    const armedCars = cars.filter((u) => u.armed && !u.disabled && u.role !== 'leave').length;
    if (armedCars < Math.max(1, ARMED_CARS[lv]!) && now >= aruReadyAt) {
      const u = spawnPursuer((heat()?.phase ?? 'none') === 'pursuit' ? 'pursue' : 'search');
      if (u) {
        u.armed = true;
        // One marksman (a rifle, longer reach, harder hits) once the whole borough is out.
        u.marksman = lv >= 4 && !cars.some((o) => o.marksman && !o.disabled);
      }
      aruReadyAt = now + (u ? 6 : 0.7);
    }
  }

  /**
   * The passenger of an armed car shoots at the suspect's CAR from the window (docs/POLICE-AI-POLISH.md): the
   * tyres from the level the armed cars arrive, the bodywork (and the driver through the glass, half the time)
   * from SHOOT_CAR_BODY_LEVEL. Fair and readable: a visible wind-up first (the aim line and the shout), only
   * from a car on screen with a clear line within range, one shot at a time across the force, a fast target
   * is hard to hit, and lying low or breaking the line stops it.
   */
  function carGun(u: CarUnit, dt: number): void {
    const t = target();
    const v = u.vehicle;
    const pv = t.vehicle;
    const range = u.marksman ? MARKSMAN_RANGE : CAR_SHOT_RANGE;
    const d = Math.hypot(t.pos.x - v.pos.x, t.pos.z - v.pos.z);
    const ok =
      armedAuthorised && u.armed && !u.disabled && u.role === 'pursue' && u.crewInCar >= 2 && !!pv && !pv.destroyed && u.seesPlayer && d <= range && d > 4 && !heat()?.hidden && outcome === 'none' && view.isVisible(v.pos.x, v.pos.z, 2) && ctx.physics.lineOfSight(v.pos, t.pos);
    if (!ok) {
      u.aim = Math.max(0, u.aim - dt * 2);
      return;
    }
    const now = ctx.state.time;
    if (u.aim === 0) ctx.events.emit('policeAim', { unitId: u.id, pos: { x: v.pos.x, z: v.pos.z }, from: 'car' });
    u.aim += dt;
    const windup = u.shots === 0 ? ARMED_AIM_FIRST : ARMED_AIM;
    if (u.aim < windup || now < u.shotReadyAt || now < armedShotReadyAt) return;
    u.shots++;
    u.shotReadyAt = now + CAR_SHOT_COOLDOWN;
    armedShotReadyAt = now + ARMED_SHOT_GAP;
    u.aim = 0.001;
    const body = level() >= SHOOT_CAR_BODY_LEVEL && rng.next() < 0.5;
    const hit = rng.next() < carShotHitChance(d, t.speed, u.marksman);
    const r = pv!.body.right();
    const side: 'left' | 'right' = (v.pos.x - pv!.pos.x) * r.x + (v.pos.z - pv!.pos.z) * r.z >= 0 ? 'right' : 'left';
    const from = { x: v.pos.x, z: v.pos.z };
    let to = { x: t.pos.x, z: t.pos.z };
    if (!hit) {
      const s = rng.next() < 0.5 ? -1 : 1;
      const dx = (t.pos.x - from.x) / Math.max(d, 1e-3);
      const dz = (t.pos.z - from.z) / Math.max(d, 1e-3);
      const off = 1.2 + rng.next() * 1.8;
      to = { x: t.pos.x + dx * 6 - dz * off * s, z: t.pos.z + dz * 6 + dx * off * s };
    }
    ctx.events.emit('policeShot', { unitId: u.id, from, to, hit, target: body ? 'car' : 'tyre' });
    if (hit) {
      if (body) {
        vehicles.applyDamage(pv!, u.marksman ? MARKSMAN_CAR_DAMAGE : CAR_SHOT_DAMAGE, side, false);
        if (rng.next() < GLASS_HIT_CHANCE) ctx.events.emit('playerHurt', { amount: u.marksman ? MARKSMAN_GLASS_DAMAGE : GLASS_HIT_DAMAGE, cause: 'shot', pos: { x: t.pos.x, z: t.pos.z } });
      } else vehicles.puncture(pv!, side, 'shot');
    }
    // Gunfire on the street: the public scatters.
    ctx.events.emit('pedPanic', { pos: from, radius: 30, reason: 'chaos' });
  }

  // ---------------------------------------------------------------- stingers (level 4 roadblocks)

  function placeStinger(lane: Lane, c: V2): void {
    const back = 9;
    const px = c.x - lane.dir.x * back;
    const pz = c.z - lane.dir.z * back;
    const across = lane.roadHalfWidth * 0.9;
    const a = { x: px - lane.left.x * across, z: pz - lane.left.z * across };
    const b = { x: px + lane.left.x * across, z: pz + lane.left.z * across };
    const s: Stinger = { id: nextStingerId++, a, b, until: ctx.state.time + ROADBLOCK_LIFE_S, hitAt: -99 };
    stingers.push(s);
    ctx.events.emit('stingerPlaced', { id: s.id, a: { ...a }, b: { ...b } });
  }

  function removeStinger(i: number): void {
    const s = stingers[i]!;
    stingers.splice(i, 1);
    ctx.events.emit('stingerRemoved', { id: s.id });
  }

  /** The player's car over a strip at speed: two tyres gone (one each side), once per strip per few seconds. */
  function updateStingers(): void {
    if (stingers.length === 0) return;
    const now = ctx.state.time;
    const t = target();
    const pv = t.vehicle;
    for (let i = stingers.length - 1; i >= 0; i--) {
      const s = stingers[i]!;
      if (now > s.until) {
        removeStinger(i);
        continue;
      }
      if (!pv || pv.speed < STINGER_SPEED || now - s.hitAt < 4) continue;
      const ex = s.b.x - s.a.x;
      const ez = s.b.z - s.a.z;
      const l2 = ex * ex + ez * ez || 1;
      const k = clamp(((pv.pos.x - s.a.x) * ex + (pv.pos.z - s.a.z) * ez) / l2, 0, 1);
      const dist = Math.hypot(s.a.x + ex * k - pv.pos.x, s.a.z + ez * k - pv.pos.z);
      if (dist > 1.4) continue;
      s.hitAt = now;
      for (let n = 0; n < STINGER_PUNCTURES; n++) vehicles.puncture(pv, n % 2 === 0 ? 'left' : 'right', 'stinger');
      ctx.events.emit('stingerHit', { id: s.id, pos: { x: pv.pos.x, z: pv.pos.z }, flats: pv.punctures.left + pv.punctures.right });
    }
  }

  /**
   * An armed officer in range with a clear line holds his ground and aims instead of running in.
   * Returns true while he does (the caller then doesn't move him).
   */
  function armedStandoff(c: FootCop, t: ReturnType<typeof target>, d: number, dt: number): boolean {
    const p = player();
    if (!armedAuthorised || !t.onFoot || !p || p.mode === 'knocked' || d > ARMED_RANGE || d < ARMED_CLOSE || !c.seesPlayer) return false;
    const b = c.body;
    if (!ctx.physics.lineOfSight(b.pos, t.pos)) return false;
    const now = ctx.state.time;
    b.setVelocity({ x: b.vel.x * 0.75, z: b.vel.z * 0.75 }, 0);
    c.yaw += clamp(angleDelta(c.yaw, Math.atan2(t.pos.x - b.pos.x, t.pos.z - b.pos.z)), -10 * dt, 10 * dt);
    if (c.aim === 0) ctx.events.emit('policeAim', { unitId: c.id, pos: { x: b.pos.x, z: b.pos.z } });
    c.aim += dt;
    // Level 3: contain only. Level 4: fire after the wind-up, one shot at a time across the force.
    const windup = c.shots === 0 ? ARMED_AIM_FIRST : ARMED_AIM;
    if (!armedCleared || c.aim < windup || now < c.shotReadyAt || now < armedShotReadyAt || heat()?.hidden) return true;
    c.shots++;
    c.firedAt = now;
    c.shotReadyAt = now + ARMED_SHOT_COOLDOWN;
    armedShotReadyAt = now + ARMED_SHOT_GAP;
    c.aim = 0.001;
    const hit = rng.next() < armedHitChance(d, t.speed);
    const from = { x: b.pos.x, z: b.pos.z };
    let to = { x: t.pos.x, z: t.pos.z };
    if (!hit) {
      // A miss goes past you: a couple of metres to one side, carrying on behind.
      const side = rng.next() < 0.5 ? -1 : 1;
      const dx = (t.pos.x - from.x) / Math.max(d, 1e-3);
      const dz = (t.pos.z - from.z) / Math.max(d, 1e-3);
      const off = 0.9 + rng.next() * 1.6;
      to = { x: t.pos.x + dx * 6 - dz * off * side, z: t.pos.z + dz * 6 + dx * off * side };
    }
    ctx.events.emit('policeShot', { unitId: c.id, from, to, hit });
    if (hit) ctx.events.emit('playerHurt', { amount: ARMED_SHOT_DAMAGE, cause: 'shot', pos: { x: t.pos.x, z: t.pos.z } });
    return true;
  }

  function despawnCar(u: CarUnit): void {
    const i = cars.indexOf(u);
    if (i >= 0) cars.splice(i, 1);
    for (const c of cops) if (c.carId === u.id) c.carId = 0;
    if (vehicles.get(u.vehicle.id)) {
      traffic?.detach(u.vehicle);
      vehicles.despawn(u.vehicle);
    }
  }

  function releaseCar(u: CarUnit): void {
    // The unit stops being police-managed but the vehicle stays (stolen by the player, wrecked...).
    const i = cars.indexOf(u);
    if (i >= 0) cars.splice(i, 1);
    u.vehicle.tags.delete('police-unit');
    delete u.vehicle.data.police;
  }

  function setRole(u: CarUnit, role: PoliceRole): void {
    if (u.role === role) return;
    const wasPatrol = u.role === 'patrol';
    u.role = role;
    u.roleSince = ctx.state.time;
    u.path.clear();
    u.goal = null;
    if (role === 'patrol') {
      u.vehicle.siren = false;
      if (!traffic?.adopt(u.vehicle, true)) u.role = 'leave';
    } else if (wasPatrol) {
      traffic?.detach(u.vehicle);
      u.vehicle.controller = 'ai';
    }
    if (role === 'pursue' || role === 'search' || role === 'respond' || role === 'roadblock') u.vehicle.siren = true;
    if (role === 'leave') u.vehicle.siren = false;
  }

  // ---------------------------------------------------------------- roadblocks

  function tryRoadblock(): boolean {
    const t = target();
    if (!net || t.onFoot || t.speed < 8) return false;
    const h = t.speed > 2 ? Math.atan2(t.vel.x, t.vel.z) : t.heading;
    const pred = roadblockSite(net, t.pos, h);
    if (!pred) return false;
    const lane = pred.lane;
    const s = pred.along;
    const c = { x: lane.a.x + lane.dir.x * s, z: lane.a.z + lane.dir.z * s };
    // Road centre line at that point.
    const toCentre = lane.kerbOffset - lane.roadHalfWidth;
    const cx = c.x + lane.left.x * toCentre;
    const cz = c.z + lane.left.z * toCentre;
    const d = Math.hypot(cx - t.pos.x, cz - t.pos.z);
    if (d < ROADBLOCK_MIN_AHEAD || view.isVisible(cx, cz, 10)) return false;
    const across = lane.roadHalfWidth * 0.5;
    const placed: CarUnit[] = [];
    for (const side of [1, -1]) {
      const px = cx + lane.left.x * across * side;
      const pz = cz + lane.left.z * across * side;
      const heading = Math.atan2(lane.dir.x, lane.dir.z) + (Math.PI / 2) * side + 0.35 * side;
      if (!ctx.physics.isShapeFree({ type: 'box', hx: 0.95, hz: 2.4 }, { x: px, z: pz }, heading, Layer.VEHICLE | Layer.STATIC)) continue;
      const v = vehicles.spawn('police', { x: px, z: pz }, { heading, controller: 'ai', occupant: { kind: 'ped', pedId: null }, tags: ['police', 'roadblock'] });
      v.siren = true;
      v.controls.handbrake = true;
      const u = addCar(v, 'roadblock');
      u.roadblockUntil = ctx.state.time + 55;
      placed.push(u);
    }
    if (placed.length > 0) {
      ctx.events.emit('roadblockSpawned', { pos: { x: cx, z: cz }, units: placed.length });
      if (level() >= STINGER_LEVEL) placeStinger(lane, { x: cx, z: cz });
    }
    return placed.length > 0;
  }

  // ---------------------------------------------------------------- foot officers

  function spawnCop(u: CarUnit): void {
    const v = u.vehicle;
    const r = v.body.right();
    // Driver's door first (offside, right-hand drive), then the passenger side.
    let pos: V2 | null = null;
    for (const side of cops.length % 2 === 0 ? [1, -1] : [-1, 1]) {
      const p = { x: v.pos.x + r.x * (v.spec.width / 2 + 0.7) * side, z: v.pos.z + r.z * (v.spec.width / 2 + 0.7) * side };
      if (ctx.physics.isShapeFree({ type: 'circle', r: 0.35 }, p, 0, Layer.ALL)) {
        pos = p;
        break;
      }
    }
    if (!pos) return;
    const c = new FootCop();
    c.id = nextId++;
    c.carId = u.id;
    c.body = ctx.physics.createBody({
      shape: { type: 'circle', r: 0.35 },
      pos,
      mass: 85,
      restitution: 0,
      friction: 0.2,
      layer: Layer.PED,
      mask: Layer.ALL,
      kind: 'ped',
      entityId: COP_ENTITY_BASE + c.id,
      canSleep: false,
    });
    c.prevX = pos.x;
    c.prevZ = pos.z;
    c.yaw = v.heading;
    c.armed = u.armed;
    c.checkX = pos.x;
    c.checkZ = pos.z;
    c.checkAt = ctx.state.time;
    cops.push(c);
    u.crewInCar--;
    if (u.crewOutSince === 0) u.crewOutSince = ctx.state.time;
  }

  function removeCop(c: FootCop): void {
    const i = cops.indexOf(c);
    if (i >= 0) cops.splice(i, 1);
    ctx.physics.removeBody(c.body);
  }

  function copFor(entityId: number): FootCop | undefined {
    return cops.find((c) => c.id === entityId - COP_ENTITY_BASE);
  }

  function updateCops(dt: number): void {
    const t = target();
    const lv = level();
    const runSpeed = FOOT_RUN[lv]!;
    for (let i = cops.length - 1; i >= 0; i--) {
      const c = cops[i]!;
      const b = c.body;
      c.prevX = b.pos.x;
      c.prevZ = b.pos.z;
      if (c.state === 'down') {
        c.downFor += dt;
        c.lean += clamp(-Math.PI / 2 + 0.1 - c.lean, -6 * dt, 6 * dt);
        b.setVelocity({ x: b.vel.x * 0.9, z: b.vel.z * 0.9 }, 0);
        if (c.downFor > 9) c.fade = Math.max(0, c.fade - dt);
        if (c.fade <= 0 || (c.downFor > 6 && !view.isVisible(b.pos.x, b.pos.z, 2))) removeCop(c);
        continue;
      }
      const car = cars.find((u) => u.id === c.carId);
      const dx = t.pos.x - b.pos.x;
      const dz = t.pos.z - b.pos.z;
      const d = Math.hypot(dx, dz);
      const now = ctx.state.time;
      // Back to the car when the suspect drives off or the heat is gone.
      // Back to the car when the suspect drives off (a man on foot can't stop a moving car) or the heat is gone.
      if (lv === 0 || (!t.onFoot && t.speed > 3 && d > 12)) c.state = car && !car.disabled ? 'return' : 'search';
      else if (lv > 0 && (t.onFoot || t.speed < 4) && d < 60 && (now >= c.recalledUntil || d < 5)) c.state = 'chase';
      let goal: V2 | null = null;
      let speed = 0;
      const pv = t.vehicle;
      if (c.state === 'chase') {
        if (pv) {
          // Suspect in a car: go to the nearer door and stand there. Leaning on the bodywork would
          // turn an 85 kg officer into a bollard the car can't push past.
          const r = pv.body.right();
          const side = (b.pos.x - pv.pos.x) * r.x + (b.pos.z - pv.pos.z) * r.z >= 0 ? 1 : -1;
          const off = pv.spec.width / 2 + 0.6;
          goal = { x: pv.pos.x + r.x * off * side, z: pv.pos.z + r.z * off * side };
          const gd = Math.hypot(goal.x - b.pos.x, goal.z - b.pos.z);
          speed = d > 4 ? runSpeed : Math.min(runSpeed * 0.7, Math.max(0.8, gd * 2.5));
        } else {
          // Run at where the suspect is going to be.
          const lead = clamp(d / 10, 0, 0.6);
          goal = { x: t.pos.x + t.vel.x * lead, z: t.pos.z + t.vel.z * lead };
          speed = d > 4 ? runSpeed : Math.max(2.5, runSpeed * 0.7);
        }
        if (!c.seesPlayer && heat()?.lastKnown) goal = heat()!.lastKnown!;
        // Progress check: an officer jogging against a wall sidesteps along it, and after a few
        // fruitless tries goes back to his car.
        if (now - c.checkAt >= 2) {
          const moved = Math.hypot(b.pos.x - c.checkX, b.pos.z - c.checkZ);
          c.stalls = moved < 0.5 && d > 3.5 && speed > 2 ? c.stalls + 1 : 0;
          c.checkX = b.pos.x;
          c.checkZ = b.pos.z;
          c.checkAt = now;
          if (c.stalls >= 3 && car && !car.disabled) {
            c.state = 'return';
            c.recalledUntil = now + 8;
            c.stalls = 0;
          } else if (c.stalls > 0) {
            c.skirtUntil = now + 1.4;
            c.skirtSide = c.stalls % 2 === 1 ? (c.id % 2 === 0 ? 1 : -1) : -c.skirtSide;
          }
        }
        if (c.state === 'chase' && now < c.skirtUntil && goal) {
          // Sidestep: aim off to one side, perpendicular to the way he was trying to go.
          const gx = goal.x - b.pos.x;
          const gz = goal.z - b.pos.z;
          const gl = Math.hypot(gx, gz) || 1;
          goal = { x: b.pos.x + (gx / gl) * 0.6 - (gz / gl) * 3 * c.skirtSide, z: b.pos.z + (gz / gl) * 0.6 + (gx / gl) * 3 * c.skirtSide };
        }
      }
      if (c.state === 'chase') {
        // Goal and speed were set above.
      } else if (c.state === 'return' && car) {
        const door = car.vehicle.pos;
        goal = door;
        speed = runSpeed * 0.85;
        if (Math.hypot(door.x - b.pos.x, door.z - b.pos.z) < 2.6) {
          car.crewInCar++;
          if (!cops.some((o) => o !== c && o.carId === car.id && o.state !== 'down')) car.crewOutSince = 0;
          removeCop(c);
          continue;
        }
      } else {
        // Lost: wander towards the last known position, then give up when nobody is looking.
        goal = heat()?.lastKnown ?? null;
        speed = 2.4;
        if (lv === 0 && !view.isVisible(b.pos.x, b.pos.z, 2)) {
          removeCop(c);
          continue;
        }
      }
      if (c.armed && c.state === 'chase' && armedStandoff(c, t, d, dt)) {
        goal = null;
      } else if (c.aim > 0) {
        c.aim = Math.max(0, c.aim - dt * 2);
      }
      if (goal) {
        const gx = goal.x - b.pos.x;
        const gz = goal.z - b.pos.z;
        const gl = Math.hypot(gx, gz);
        if (gl > 0.3) {
          const want = Math.atan2(gx, gz);
          c.yaw += clamp(angleDelta(c.yaw, want), -12 * dt, 12 * dt);
          let tx = (gx / gl) * speed;
          let tz = (gz / gl) * speed;
          if (pv) {
            // Never push into the suspect's car; get out of its way when it comes at you.
            const fwd = pv.body.forward();
            const rgt = pv.body.right();
            const rx = b.pos.x - pv.pos.x;
            const rz = b.pos.z - pv.pos.z;
            const lx = rx * rgt.x + rz * rgt.z;
            const lz = rx * fwd.x + rz * fwd.z;
            const hw = pv.spec.width / 2;
            const hl = pv.spec.length / 2;
            const nlx = lx - clamp(lx, -hw, hw);
            const nlz = lz - clamp(lz, -hl, hl);
            const nl = Math.hypot(nlx, nlz);
            if (nl > 1e-3 && nl < 0.35 + 0.3) {
              const nx = (nlx * rgt.x + nlz * fwd.x) / nl;
              const nz = (nlx * rgt.z + nlz * fwd.z) / nl;
              const into = tx * nx + tz * nz;
              if (into < 0) {
                tx -= into * nx;
                tz -= into * nz;
              }
            }
            const vF = pv.velocity.x * fwd.x + pv.velocity.z * fwd.z;
            const ahead = vF >= 0 ? lz - hl : -lz - hl;
            if (Math.abs(vF) > 1.5 && Math.abs(lx) < hw + 0.7 && ahead > -0.3 && ahead < 1.5 + Math.abs(vF) * 0.4) {
              const away = lx >= 0 ? 1 : -1;
              tx = rgt.x * away * runSpeed;
              tz = rgt.z * away * runSpeed;
            }
          }
          const k = Math.min(1, 10 * dt);
          b.setVelocity({ x: b.vel.x + (tx - b.vel.x) * k, z: b.vel.z + (tz - b.vel.z) * k }, 0);
        } else {
          b.setVelocity({ x: b.vel.x * 0.7, z: b.vel.z * 0.7 }, 0);
        }
      }
      b.angle = 0;
      c.speed = Math.hypot(b.vel.x, b.vel.z);
      c.phase += c.speed * dt * 4.4;
      c.lean += clamp(0 - c.lean, -3 * dt, 3 * dt);
      if (Math.hypot(b.pos.x - t.pos.x, b.pos.z - t.pos.z) > DESPAWN_R && !view.isVisible(b.pos.x, b.pos.z, 2)) removeCop(c);
    }
  }

  function knockCop(c: FootCop, byPlayer: boolean, vel: V2): void {
    if (c.state === 'down') return;
    c.state = 'down';
    c.downFor = 0;
    c.body.layer = Layer.DEBRIS;
    c.body.mask = Layer.STATIC;
    c.body.setVelocity({ x: vel.x * 0.5, z: vel.z * 0.5 }, 0);
    const car = cars.find((u) => u.id === c.carId);
    if (car) car.crewInCar = Math.max(0, car.crewInCar);
    ctx.events.emit('officerDown', { unitId: c.id, pos: { x: c.body.pos.x, z: c.body.pos.z }, byPlayer });
    if (byPlayer) ctx.events.emit('crime', { type: 'hitPolice', pos: { x: c.body.pos.x, z: c.body.pos.z }, severity: 4, byPlayer: true });
    ctx.events.emit('pedPanic', { pos: { x: c.body.pos.x, z: c.body.pos.z }, radius: 24, reason: 'hit' });
  }

  // ---------------------------------------------------------------- car unit AI

  /**
   * Tactics by distance rank, re-ranked every TACTIC_RERANK seconds or when the set of pursuers changes
   * (docs/POLICE-AI-POLISH.md #3): a unit keeps its job (and its aim point) long enough to carry it out,
   * and consecutive ranks work opposite sides of the suspect.
   */
  function assignTactics(): void {
    const lv = level();
    const t = target();
    const now = ctx.state.time;
    const pursuers = cars.filter((u) => u.role === 'pursue' && !u.disabled);
    const key = `${lv}:${pursuers.map((u) => u.id).join(',')}`;
    if (key === rankedKey && now - rankedAt < TACTIC_RERANK) return;
    rankedKey = key;
    rankedAt = now;
    pursuers.sort((a, b) => dist2(a.vehicle.pos, t.pos) - dist2(b.vehicle.pos, t.pos));
    const rx = Math.cos(t.heading);
    const rz = -Math.sin(t.heading);
    const sides = sidesForRanks(pursuers.map((u) => (u.vehicle.pos.x - t.pos.x) * rx + (u.vehicle.pos.z - t.pos.z) * rz));
    pursuers.forEach((u, i) => {
      u.tactic = tacticForRank(lv, i);
      u.side = sides[i]!;
    });
  }

  function driveUnit(u: CarUnit, dt: number): void {
    const v = u.vehicle;
    const now = ctx.state.time;
    const ctl = v.controls;
    const lv = level();
    const t = target();
    if (u.role === 'patrol' || u.disabled) return;
    const dToPlayer = Math.hypot(t.pos.x - v.pos.x, t.pos.z - v.pos.z);
    if (u.role === 'roadblock') {
      ctl.throttle = 0;
      ctl.steer = 0;
      ctl.handbrake = true;
      return;
    }
    // Crew out on foot: the car waits with its lamps going while the officers work the suspect nearby.
    // With a driver still aboard and the suspect off up the road, it shadows them instead of sitting
    // there for the 12 s recall (docs/POLICE-AI-POLISH.md #7): the officers come back to it.
    if (u.crewInCar < u.crew && cops.some((c) => c.carId === u.id) && (u.crewInCar <= 0 || dToPlayer < 25)) {
      ctl.throttle = 0;
      ctl.steer = 0;
      ctl.handbrake = true;
      // Not forever: officers who've got nowhere near the suspect are called back to the car.
      if (u.crewOutSince > 0 && now - u.crewOutSince > CREW_WAIT_MAX) {
        for (const c of cops) {
          if (c.carId !== u.id || c.state !== 'chase') continue;
          if (Math.hypot(c.body.pos.x - t.pos.x, c.body.pos.z - t.pos.z) > 8) {
            c.state = 'return';
            c.recalledUntil = now + 8;
          }
        }
        u.crewOutSince = now - CREW_WAIT_MAX + 4;
      }
      return;
    }
    if (now < u.recoverUntil) {
      ctl.throttle = -0.7;
      ctl.steer = u.recoverSteer;
      ctl.handbrake = false;
      if (v.body.sleeping) v.body.wake();
      return;
    }
    if (now < u.backoffUntil) {
      ctl.throttle = -0.4;
      ctl.steer = 0;
      ctl.handbrake = false;
      return;
    }
    const maxV = v.spec.maxSpeed;
    let desired = maxV * PURSUIT_SPEED[lv]!;
    let goal: V2 | null = null;
    let direct = false;
    const known = heat()?.lastKnown ?? null;

    if (u.role === 'pursue') {
      const fresh = u.seesPlayer || now - u.lastSawAt < 1.5;
      // Offline fairness (docs/POLICE.md §12): a crew that has just lost sight of you steers for where
      // they SAW you going (dead reckoning from the last sighting), not for where you really are, so a
      // corner taken out of their sight actually throws them.
      const est = u.seesPlayer ? t : crewEstimate(u, t, now);
      const dEst = Math.hypot(est.pos.x - v.pos.x, est.pos.z - v.pos.z);
      // Just recovered from a wedge: follow the road for a moment instead of cutting straight back at the suspect.
      if (fresh && dEst < 75 && now >= u.routeUntil) {
        direct = true;
        // Nobody rams a man on foot: drive up, stop short, and let the crew do the running.
        // A PIT comes from behind or alongside; from in front a rammer blocks instead of going head-on.
        const along = est.speed > 3 ? ((v.pos.x - est.pos.x) * est.vel.x + (v.pos.z - est.pos.z) * est.vel.z) / est.speed : 0;
        const inFront = along > 0;
        // A box unit that has got well ahead stops boxing and blocks (the head-on rule below).
        const boxDone = u.tactic === 'box' && along > 0.7 * dEst;
        // A PIT comes from behind, roughly in line: a rammer crossing the suspect's path at an angle would
        // T-bone them, so it falls in behind first (docs/POLICE-AI-POLISH.md #3).
        const crossing = Math.abs(angleDelta(v.heading, est.heading)) > RAM_MAX_ANGLE;
        const tactic: Tactic = t.onFoot ? 'chase' : (u.tactic === 'ram' && (inFront || crossing)) || boxDone ? 'chase' : u.tactic;
        tacticAim(tactic, est, v.pos, u.side, aim);
        goal = aim;
        if (tactic === 'ram') desired = maxV * Math.max(PURSUIT_SPEED[lv]!, 0.92);
        // Closing on a stopped / slow suspect: pull up alongside instead of ploughing in.
        if (tactic !== 'ram' && t.speed < 4 && dToPlayer < 16) desired = Math.max(1.5, t.speed + (dToPlayer - 7) * 0.8);
        // Pull up on stopping distance short of a pedestrian suspect.
        if (t.onFoot) desired = Math.min(desired, Math.sqrt(2 * 5 * Math.max(0, dToPlayer - OFFLINE_FOOT_STANDOFF[lv]!)));
        // Only rammers want contact. Everyone else draws alongside, and a car coming head-on stops
        // across the suspect's path instead of hitting it.
        if (!t.onFoot && tactic !== 'ram' && tactic !== 'box' && dToPlayer < 22) {
          const dx = (t.pos.x - v.pos.x) / Math.max(dToPlayer, 0.1);
          const dz = (t.pos.z - v.pos.z) / Math.max(dToPlayer, 0.1);
          const closing = (v.velocity.x - t.vel.x) * dx + (v.velocity.z - t.vel.z) * dz;
          const headOn = t.vel.x * dx + t.vel.z * dz < -3;
          if (headOn) desired = Math.min(desired, Math.max(0, (dToPlayer - 12) * 0.5));
          else if (closing > 3) desired = Math.min(desired, Math.max(2, t.speed + (dToPlayer - 8) * 0.4));
          // Aim beside the suspect, on our offside.
          const rx = -Math.cos(v.heading);
          const rz = Math.sin(v.heading);
          aim.x += rx * 3;
          aim.z += rz * 3;
        }
        // Nudge, don't torpedo: whatever the tactic, never close on a suspect in a car faster than
        // braking can take off before the stand-off distance. Rammers arrive ~5 m/s faster than the
        // suspect, boxers ~2.5 m/s, everyone else matches speed short of contact.
        if (!t.onFoot && dToPlayer > 0.1) {
          const ux = (t.pos.x - v.pos.x) / dToPlayer;
          const uz = (t.pos.z - v.pos.z) / dToPlayer;
          const away = t.vel.x * ux + t.vel.z * uz;
          const allowed = tactic === 'ram' ? 5 : tactic === 'box' ? 2.5 : 0;
          const standoff = tactic === 'ram' ? 4 : 6;
          const closeMax = Math.sqrt(allowed * allowed + 2 * APPROACH_DECEL * Math.max(0, dToPlayer - standoff));
          const hu = Math.sin(v.heading) * ux + Math.cos(v.heading) * uz;
          if (hu > 0.15) desired = Math.min(desired, Math.max(0, (away + closeMax) / hu));
        }
      } else {
        goal = known ?? t.pos;
      }
    } else if (u.role === 'search') {
      const area = heat()?.search;
      if (!u.goal || Math.hypot(u.goal.x - v.pos.x, u.goal.z - v.pos.z) < 10 || now - u.roleSince > 14) {
        u.goal = pickSearchPoint(u, area ? area.center : (known ?? t.pos), area ? area.radius : 40);
        u.roleSince = now;
        u.path.clear();
      }
      goal = u.goal;
      desired = maxV * 0.42;
    } else if (u.role === 'respond') {
      goal = incident ? incident.pos : null;
      desired = maxV * 0.5;
      if (goal && Math.hypot(goal.x - v.pos.x, goal.z - v.pos.z) < 14) {
        desired = 0;
        if (u.lingerUntil === 0) u.lingerUntil = now + 8;
      }
      if (!goal || (u.lingerUntil > 0 && now > u.lingerUntil)) {
        u.lingerUntil = 0;
        setRole(u, 'patrol');
        return;
      }
    } else if (u.role === 'leave') {
      if (!u.goal) {
        // Drive off away from the player.
        const away = { x: v.pos.x + (v.pos.x - t.pos.x), z: v.pos.z + (v.pos.z - t.pos.z) };
        u.goal = net ? net.layout.roads.nodes[nearestNode(net, away)]!.pos : away;
      }
      goal = u.goal;
      desired = maxV * 0.4;
    }
    if (!goal) {
      ctl.throttle = 0;
      ctl.handbrake = true;
      return;
    }
    if (!direct && net) {
      // Route along the roads; replan when the goal moved or every few seconds.
      if (!u.path.valid || now - u.path.plannedAt > 3 || Math.hypot(u.path.goal.x - goal.x, u.path.goal.z - goal.z) > 20) {
        u.path.plan(net, v.pos, v.heading, goal, now);
      }
      if (u.path.valid) {
        const look = clamp(5 + v.speed * 0.55, 6, 16);
        u.path.lookahead(v.pos, look, aim);
        if (u.path.remaining(v.pos) < 8 && u.role !== 'leave') desired = Math.min(desired, u.role === 'pursue' ? desired : 4);
        goal = aim;
      }
    }
    // Alleys: a police saloon can't do much in there.
    const onLane = net?.nearestLane(v.pos);
    if (onLane && onLane.lane.alley && Math.abs(onLane.lateral) < 4) desired = Math.min(desired, 7);
    // Don't plough through civilians below level 4 (and never while just searching).
    let queued = false;
    if (lv < 4 || u.role !== 'pursue') {
      const reach = 6 + v.speed * 0.7;
      const ignore = t.vehicle && direct ? t.vehicle.body.id : null;
      const ahead = vehicleAhead(v, ctx.physics, ignore, reach);
      if (ahead < Infinity) {
        // Blue lamps on: go round traffic that has pulled over / is queueing, on the offside.
        const passOff = -2.8; // right of travel (British roads: the offside)
        const clearRight = u.vehicle.siren && vehicleAhead(v, ctx.physics, ignore, reach + 4, passOff) === Infinity;
        // Offside blocked too (a car abandoned across the road): squeeze past on the nearside at a crawl.
        const clearLeft = !clearRight && v.speed < 4 && vehicleAhead(v, ctx.physics, ignore, reach + 4, -passOff) === Infinity;
        if ((clearRight || clearLeft) && goal) {
          const rx = -Math.cos(v.heading);
          const rz = Math.sin(v.heading);
          const off = clearRight ? passOff : -passOff;
          aim.x = goal.x - rx * off;
          aim.z = goal.z - rz * off;
          goal = aim;
          desired = Math.min(desired, clearRight ? 11 : 4);
        } else {
          desired = Math.min(desired, Math.max(2, Math.sqrt(2 * 6 * Math.max(0, ahead - 2))));
          // Patient queueing is fine, but not forever.
          if (v.speed < 1) queued = true;
        }
      }
    }
    // Dodging after a recovery: aim a few metres to the side of where the wedge was.
    if (now < u.dodgeUntil && goal) {
      const rx = -Math.cos(v.heading);
      const rz = Math.sin(v.heading);
      dodgeAim.x = goal.x + rx * DODGE_OFFSET * u.dodgeSide;
      dodgeAim.z = goal.z + rz * DODGE_OFFSET * u.dodgeSide;
      goal = dodgeAim;
    }
    // Pedestrians in the way: officers brake for the public (at level 4 only for the ones right in front).
    const peds = ctx.services.tryGet('peds');
    if (peds && v.speed > 1) {
      const fx = Math.sin(v.heading);
      const fz = Math.cos(v.heading);
      const reach = (lv >= 4 && u.role === 'pursue' ? 4 : 6) + v.speed * 0.7;
      const cx = v.pos.x + fx * (reach / 2);
      const cz = v.pos.z + fz * (reach / 2);
      let nearest = Infinity;
      peds.forEachNear(cx, cz, reach / 2 + 2, (px, pz) => {
        const dx = px - v.pos.x;
        const dz = pz - v.pos.z;
        const along = dx * fx + dz * fz;
        const lateral = Math.abs(dx * fz - dz * fx);
        if (along > 0 && along < reach && lateral < v.spec.width / 2 + 1.2) nearest = Math.min(nearest, along);
      });
      if (nearest < Infinity) desired = Math.min(desired, Math.max(0, Math.sqrt(2 * 8 * Math.max(0, nearest - v.spec.length / 2 - 1.5))));
    }
    const res = driveToward(v, goal, desired, ctx.physics, ctl, { reverseOk: direct });

    // Stuck: asking for power but getting nowhere (pinned against a car, a wall, a corner). A searching
    // unit queued behind something gives up on that street sooner: its goal was a guess anyway.
    u.queueFor = queued ? u.queueFor + dt : 0;
    const queueMax = u.role === 'search' ? 3 : 6;
    if ((desired > 3 && ctl.throttle > 0.4 && v.speed < 1.6) || u.queueFor > queueMax) {
      u.stuckFor += dt;
      if (u.stuckFor > 1.6 || u.queueFor > queueMax) {
        u.queueFor = 0;
        u.stuckFor = 0;
        beginRecovery(u, res.angle, now);
      }
    } else {
      u.stuckFor = 0;
    }
  }

  /**
   * Reverse out of a wedge, then dodge sideways and follow the road for a moment (docs/POLICE-AI-POLISH.md #1):
   * the second time at the same spot longer and the other way. A searching unit stuck twice picks a new goal.
   */
  function beginRecovery(u: CarUnit, aimAngle: number, now: number): void {
    const v = u.vehicle;
    const f = v.body.forward();
    const probe = { x: v.pos.x + f.x * v.spec.length * 0.9, z: v.pos.z + f.z * v.spec.length * 0.9 };
    const box = { type: 'box' as const, hx: v.spec.width / 2, hz: v.spec.length / 2 };
    const mask = Layer.STATIC | Layer.VEHICLE | Layer.PROP;
    const openLeft = ctx.physics.isShapeFree(box, probe, v.heading + 0.45, mask, v.body);
    const openRight = ctx.physics.isShapeFree(box, probe, v.heading - 0.45, mask, v.body);
    const r = planRecovery(u.stuck, v.pos, now, openLeft, openRight, aimAngle);
    u.recoverUntil = now + r.reverse;
    u.recoverSteer = -r.side;
    u.dodgeUntil = u.recoverUntil + r.dodge;
    u.dodgeSide = r.side;
    u.routeUntil = u.recoverUntil + 3;
    u.path.clear();
    if (u.role === 'search' && u.stuck.count >= 2) u.goal = null;
  }

  /** A junction to search next: towards where the suspect was last seen going, not where a colleague is already heading. */
  function pickSearchPoint(u: CarUnit, center: V2, radius: number): V2 {
    if (!net) return center;
    const taken: V2[] = [];
    for (const o of cars) if (o !== u && o.role === 'search' && o.goal) taken.push(o.goal);
    return pickSearchGoal(searchNodes, center, radius, heat()?.lastKnownVel ?? null, taken, u.vehicle.pos, () => rng.next()) ?? center;
  }

  // ---------------------------------------------------------------- unit housekeeping

  /**
   * Book this step's health loss to the player or to the unit's own crashes. The player's: a contact the
   * collision handler blamed on them (`blameAt`, their ram), or damage the vehicles module marked as theirs
   * (`lastPlayerHitTime`: a shot, a blast they caused, any contact with their car) unless that contact was
   * excused (the unit drove into them). Within CONTACT_WINDOW, so the order of the listeners doesn't matter.
   */
  function trackDamage(u: CarUnit, now: number): void {
    const v = u.vehicle;
    const lost = u.lastHealth - v.health;
    u.lastHealth = v.health;
    if (!(lost > 0)) return;
    const blamed = now - u.blameAt <= CONTACT_WINDOW;
    const marked = now - v.lastPlayerHitTime <= CONTACT_WINDOW && now - u.excusedAt > CONTACT_WINDOW;
    if (blamed || marked) {
      u.playerDamage += lost;
      u.playerHitAt = now;
      return;
    }
    u.crashDamage += lost;
    if (now - u.crashBurstAt > KNOCKOUT_WINDOW) {
      u.crashBurst = 0;
      u.crashBurstAt = now;
    }
    u.crashBurst += lost;
    if (u.role === 'pursue' && level() > 0 && u.crashBurst >= v.spec.maxHealth * UNIT_KNOCKOUT) u.knockedOut = true;
  }

  /**
   * "Laid on the tarmac" (docs/POLICE.md §14): a unit that was chasing you (pursuit role, saw you lately)
   * went out mostly on its own crashes, and you haven't rammed or shot it for FORCED_CLEAN_FOR seconds.
   */
  function forcedCrash(u: CarUnit, now: number): boolean {
    if (level() === 0 || u.role !== 'pursue') return false;
    if (now - u.lastSawAt > FORCED_SAW_WITHIN) return false;
    if (now - u.playerHitAt < FORCED_CLEAN_FOR || now - u.blameAt < FORCED_CLEAN_FOR) return false;
    return u.crashDamage > u.playerDamage;
  }

  function maintainUnits(dt: number): void {
    const now = ctx.state.time;
    const t = target();
    for (let i = cars.length - 1; i >= 0; i--) {
      const u = cars[i]!;
      const v = u.vehicle;
      if (!vehicles.get(v.id)) {
        cars.splice(i, 1);
        continue;
      }
      // Player nicked the police car: it's theirs now.
      if (v.controller === 'player' || v.occupant?.kind === 'player') {
        releaseCar(u);
        continue;
      }
      if (!u.disabled) trackDamage(u, now);
      if (!u.disabled && (u.knockedOut || v.destroyed || v.health < v.spec.maxHealth * 0.22 || v.condition === 'burning')) {
        const forced = forcedCrash(u, now);
        // Laid on the tarmac: written off, it sits there steaming (a wreck already burns).
        if (forced && !v.destroyed) {
          v.health = Math.min(v.health, v.spec.maxHealth * WRITTEN_OFF);
          if (v.condition === 'ok' || v.condition === 'smoking') v.condition = 'heavySmoke';
        }
        u.disabled = true;
        u.disabledAt = now;
        u.role = 'disabled';
        traffic?.detach(v);
        v.siren = false;
        v.controller = 'none';
        v.occupant = null;
        ctx.events.emit('policeUnitDisabled', { unitId: u.id, vehicleId: v.id, pos: { x: v.pos.x, z: v.pos.z }, byPlayer: level() > 0, forced });
        // Takedown moment (docs/POLICE-AI-POLISH.md, Burnout's beat): a unit you put on the tarmac close by gets a
        // freeze-frame and a camera punch.
        if (forced && Math.hypot(v.pos.x - t.pos.x, v.pos.z - t.pos.z) < TAKEDOWN_RANGE) {
          ctx.services.tryGet('juice')?.hitStop(TAKEDOWN_SLOWMO_S);
          ctx.services.tryGet('cameraRig')?.shake(0.55);
        }
      }
      // Patrol that traffic dropped (e.g. stuck & retired): take it back as a leaving unit.
      if (u.role === 'patrol' && traffic && !traffic.isDriving(v)) {
        if (!traffic.adopt(v, true)) setRole(u, 'leave');
      }
      const d = Math.hypot(v.pos.x - t.pos.x, v.pos.z - t.pos.z);
      const hidden = !view.isVisible(v.pos.x, v.pos.z, 6);
      if (hidden && d > DESPAWN_R) {
        despawnCar(u);
        continue;
      }
      // Wrecked police cars stay on the street a while (a nice trophy) before being cleared.
      if ((u.role === 'leave' || (u.disabled && now - u.disabledAt > 25)) && hidden && d > 55) {
        despawnCar(u);
        continue;
      }
      if (u.role === 'roadblock') {
        // Passed it, plainly driving away from it, or standing for ages: break up the block and join in.
        const behind = (v.pos.x - t.pos.x) * t.vel.x + (v.pos.z - t.pos.z) * t.vel.z < 0;
        u.missFor = behind && d > 70 ? u.missFor + dt : 0;
        if ((behind && d > 45) || u.missFor > ROADBLOCK_MISS_S || now > u.roadblockUntil) setRole(u, level() >= 2 ? 'pursue' : 'leave');
      }
    }
  }

  function composeForce(): void {
    const now = ctx.state.time;
    const lv = level();
    const h = heat();
    const phase = h?.phase ?? 'none';
    const active = cars.filter((u) => !u.disabled);
    if (lv === 0) {
      // Stand down: one or two go back on patrol, the rest leave.
      let patrols = active.filter((u) => u.role === 'patrol' || u.role === 'respond').length;
      for (const u of active) {
        if (u.role === 'pursue' || u.role === 'search' || u.role === 'roadblock') {
          if (patrols < CAR_CAP[0] && u.role !== 'roadblock') {
            setRole(u, 'patrol');
            patrols++;
          } else setRole(u, 'leave');
        }
      }
      if (patrols < CAR_CAP[0] && now >= patrolReadyAt) {
        patrolReadyAt = now + SPAWN_INTERVAL[0];
        const slot = laneSlotOffscreen(() => true, SPAWN_MIN_R, SPAWN_MAX_R + 40);
        if (!(slot && spawnPatrolAt(slot.lane, slot.s))) patrolReadyAt = now + 1;
      }
      return;
    }
    const role: PoliceRole = phase === 'pursuit' ? 'pursue' : 'search';
    // Everyone not blocking roads joins in (patrols first, they're closest to the action).
    for (const u of active) {
      if (u.role === 'roadblock' || u.role === role) continue;
      if (u.role === 'leave' && (now < u.standBackUntil || active.filter((x) => x.role === role).length >= CAR_CAP[lv]!)) continue;
      setRole(u, role);
    }
    const working = active.filter((u) => u.role === 'pursue' || u.role === 'search').length;
    if (working < CAR_CAP[lv]! && now >= spawnReadyAt) {
      // Off-screen slots can be scarce near the map edge: retry soon if none was found.
      // The van (four constables) once the whole borough is out (docs/POLICE-AI-POLISH.md).
      const vans = active.filter((u) => u.crew === VAN_CREW && u.role !== 'leave').length;
      const spec = vans < VAN_CAP[lv]! ? VAN_SPEC : 'police';
      spawnReadyAt = now + (spawnPursuer(role, spec) ? SPAWN_INTERVAL[lv]! : 0.5);
    }
    // Over the cap (level dropped): the furthest ones head off.
    if (working > CAR_CAP[lv]! + 1) {
      const t = target();
      const sorted = active.filter((u) => u.role === role).sort((a, b) => dist2(b.vehicle.pos, t.pos) - dist2(a.vehicle.pos, t.pos));
      for (let i = 0; i < working - CAR_CAP[lv]!; i++) setRole(sorted[i]!, 'leave');
    }
    const blocks = cars.filter((u) => u.role === 'roadblock').length;
    if (phase === 'pursuit' && blocks / 2 < ROADBLOCK_CAP[lv]! && now >= roadblockReadyAt) {
      // Only when a block could actually be placed does the interval start; otherwise look again shortly.
      roadblockReadyAt = now + (tryRoadblock() ? ROADBLOCK_INTERVAL : 1);
    }
  }

  function deployFootOfficers(): void {
    const lv = level();
    if (lv === 0) return;
    const t = target();
    const footCount = cops.filter((c) => c.state !== 'down').length;
    if (footCount >= FOOT_CAP[lv]!) return;
    // A suspect on foot gets chased on foot straight away; a car has to have actually stopped first
    // (a stall, a three-point turn or a queue at the lights isn't a surrender).
    if (!t.onFoot && (t.speed > 2 || stillFor < DISMOUNT_DELAY[lv]!)) return;
    for (const u of cars) {
      if (u.disabled || u.role !== 'pursue' || u.crewInCar <= 0) continue;
      // Level 1 is a word with the driver: one officer per car. A suspect running off is chased by one
      // officer per car too: the driver stays at the wheel to shadow them (docs/POLICE-AI-POLISH.md #7).
      if (u.crewInCar < u.crew && ((lv <= 1 && !t.onFoot) || (t.onFoot && t.speed > 4))) continue;
      if (u.vehicle.speed > 2.5) continue;
      if (Math.hypot(u.vehicle.pos.x - t.pos.x, u.vehicle.pos.z - t.pos.z) > 22) continue;
      spawnCop(u);
      if (cops.filter((c) => c.state !== 'down').length >= FOOT_CAP[lv]!) return;
    }
  }

  // ---------------------------------------------------------------- arrest / wasted / respawn

  function respawnPoint(kind: 'busted' | 'wasted'): V2 {
    const pois = layout.pois;
    const find = (pred: (p: PointOfInterest) => boolean): PointOfInterest | undefined => pois.find(pred);
    const station = find((p) => p.kind === 'policeStation');
    const hospital = find((p) => /hospital|infirmary/i.test(p.id) || /hospital|infirmary/i.test(p.name));
    const safe = find((p) => p.kind === 'safehouse');
    const poi = kind === 'busted' ? (station ?? safe) : (hospital ?? safe);
    if (!poi) return { ...layout.playerSpawn.pos };
    // Step out in front of the door.
    return { x: poi.pos.x + Math.sin(poi.heading) * (poi.radius + 1), z: poi.pos.z + Math.cos(poi.heading) * (poi.radius + 1) };
  }

  function applyPenalty(amount: number, reason: string): number {
    const st = ctx.state;
    const pay = Math.min(amount, Math.max(0, st.money));
    // The economy module, once present, applies penalties itself from the busted/wasted events.
    if (!ctx.services.has('economy' as never) && pay > 0) {
      st.money -= pay;
      ctx.events.emit('moneyChanged', { amount: st.money, delta: -pay, reason });
    }
    return pay;
  }

  function beginOutcome(kind: 'busted' | 'wasted', cause: string): void {
    if (outcome !== 'none') return;
    outcome = kind;
    outcomeTimer = RESPAWN_DELAY;
    const p = player();
    const pos = { ...playerPos() };
    if (p) {
      if (p.mode === 'driving') {
        const v = p.vehicle;
        p.exitVehicle();
        if (v) {
          v.controls.throttle = 0;
          v.controls.handbrake = true;
        }
      }
      p.controls.acquire('policeOutcome');
    }
    const money = Math.max(0, ctx.state.money);
    const penalty = kind === 'busted' ? Math.max(50, Math.round(money * 0.1)) : Math.max(100, Math.round(money * 0.15));
    const charged = Math.min(penalty, money);
    outcomePenalty = charged;
    if (kind === 'busted') ctx.events.emit('playerBusted', { pos, penalty: charged });
    else ctx.events.emit('playerWasted', { pos, penalty: charged, cause });
  }

  function finishOutcome(): void {
    const kind = outcome as 'busted' | 'wasted';
    const p = player();
    const pos = respawnPoint(kind);
    applyPenalty(outcomePenalty, kind === 'busted' ? 'Nicked: fine and legal fees' : 'Wasted: hospital bill');
    heat()?.clear();
    for (const u of cars) if (!u.disabled && u.role !== 'patrol') setRole(u, 'leave');
    for (const c of cops.slice()) removeCop(c);
    for (let i = stingers.length - 1; i >= 0; i--) removeStinger(i);
    if (p) {
      p.teleport(pos);
      p.controls.release('policeOutcome');
    }
    health = HEALTH_MAX;
    bust = 0;
    stillFor = 0;
    resetArmed();
    outcome = 'none';
    ctx.events.emit('playerRespawned', { pos, reason: kind });
  }

  function updateBust(dt: number): void {
    const lv = level();
    const p = player();
    if (!p || lv === 0) {
      bust = Math.max(0, bust - dt * 2);
      return;
    }
    const t = target();
    let rate = 0;
    if (t.onFoot) {
      for (const c of cops) {
        if (c.state === 'down') continue;
        if (Math.hypot(c.body.pos.x - t.pos.x, c.body.pos.z - t.pos.z) < 1.5 && t.speed < 3.4) rate = Math.max(rate, 1 / BUST_TIME_FOOT);
      }
      if (p.mode === 'knocked') rate *= 1.6;
    } else if (t.speed < 1.0) {
      // In a car you are nicked only once you've given up: stopped, with an officer at the door (or a
      // unit pulled up beside you after you've sat there a moment). Flooring it against a unit or a wall
      // is a struggle, not a surrender: the meter crawls and only officers at the doors can finish it.
      const ctl = t.vehicle?.controls;
      const struggling = !!ctl && Math.abs(ctl.throttle) > 0.3 && !ctl.handbrake;
      let atDoor = false;
      for (const c of cops) {
        if (c.state !== 'down' && Math.hypot(c.body.pos.x - t.pos.x, c.body.pos.z - t.pos.z) < 3.2) atDoor = true;
      }
      let pulledOver = false;
      if (!struggling && stillFor >= BUST_PULLED_OVER) {
        for (const u of cars) {
          if (u.disabled || u.role === 'patrol' || u.crewInCar <= 0 || u.vehicle.speed > 1) continue;
          if (Math.hypot(u.vehicle.pos.x - t.pos.x, u.vehicle.pos.z - t.pos.z) < 7.5) pulledOver = true;
        }
      }
      if (atDoor || pulledOver) rate = (1 / BUST_TIME_CAR[lv]!) * (struggling ? BUST_STRUGGLE_RATE : 1);
    }
    if (rate > 0) bust = Math.min(1, bust + rate * dt);
    // Getting moving again shakes them off quickly.
    else bust = Math.max(0, bust - dt * (t.speed > 2 ? 3 : 1.5));
    if (bust >= 1) beginOutcome('busted', 'arrest');
  }

  function updateHealth(dt: number): void {
    if (outcome !== 'none') return;
    if (ctx.state.time - lastHurtAt > 6 && health < HEALTH_MAX) health = Math.min(HEALTH_MAX, health + 3 * dt);
    if (health <= 0) beginOutcome('wasted', 'injuries');
  }

  // ---------------------------------------------------------------- service

  function unitInfo(u: CarUnit): PoliceUnitInfo {
    return {
      id: u.id,
      kind: 'car',
      role: u.role,
      x: +u.vehicle.pos.x.toFixed(2),
      z: +u.vehicle.pos.z.toFixed(2),
      speed: +u.vehicle.speed.toFixed(2),
      vehicleId: u.vehicle.id,
      siren: u.vehicle.siren,
      health: +u.vehicle.health.toFixed(1),
      seesPlayer: u.seesPlayer,
      tactic: u.role === 'pursue' ? u.tactic : '',
      armed: u.armed,
      aiming: u.armed && u.aim > 0 ? +clamp(u.aim / (u.shots === 0 ? ARMED_AIM_FIRST : ARMED_AIM), 0, 1).toFixed(2) : 0,
      crewOut: u.crewInCar < u.crew,
    };
  }

  const service: PoliceService = {
    observers() {
      return observers;
    },
    setSeers(ids) {
      seers.clear();
      for (const id of ids) seers.add(id);
      const now = ctx.state.time;
      const t = target();
      for (const u of cars) {
        u.seesPlayer = seers.has(u.id);
        if (u.seesPlayer) {
          u.lastSawAt = now;
          u.sawPos.x = t.pos.x;
          u.sawPos.z = t.pos.z;
          u.sawVel.x = t.vel.x;
          u.sawVel.z = t.vel.z;
          u.sawHeading = t.heading;
        }
      }
      for (const c of cops) c.seesPlayer = seers.has(c.id);
    },
    units() {
      if (remote) return remote.units().map(remoteUnitInfo);
      const out: PoliceUnitInfo[] = cars.map(unitInfo);
      for (const c of cops) {
        out.push({
          id: c.id,
          kind: 'foot',
          role: c.state === 'down' ? 'disabled' : 'pursue',
          x: +c.body.pos.x.toFixed(2),
          z: +c.body.pos.z.toFixed(2),
          speed: +c.speed.toFixed(2),
          vehicleId: null,
          siren: false,
          health: c.state === 'down' ? 0 : 100,
          seesPlayer: c.seesPlayer,
          tactic: c.state,
          armed: c.armed,
          aiming: +clamp(c.aim / (c.shots === 0 ? ARMED_AIM_FIRST : ARMED_AIM), 0, 1).toFixed(2),
          crewOut: false,
        });
      }
      return out;
    },
    info(): PoliceInfo {
      const h = heat();
      const hi = h?.info();
      const units = service.units();
      return {
        heat: h?.level ?? 0,
        phase: h?.phase ?? 'none',
        units,
        cars: remote ? units.filter((u) => u.kind === 'car' && u.role !== 'disabled').length : cars.filter((u) => !u.disabled).length,
        foot: remote ? units.filter((u) => u.kind === 'foot' && u.role !== 'disabled').length : cops.filter((c) => c.state !== 'down').length,
        roadblocks: remote ? 0 : cars.filter((u) => u.role === 'roadblock').length,
        bustProgress: +service.bustProgress.toFixed(3),
        outcome,
        health: +health.toFixed(1),
        armedAuthorised,
        search: hi?.search ?? null,
        stats: { ...stats },
      };
    },
    get bustProgress() {
      return remote ? remote.bustProgress : bust;
    },
    get outcome() {
      return outcome;
    },
    setOutcomeGate(gate) {
      outcomeGate = gate;
    },
    setRemote(source) {
      if (source === remote) return;
      remote = source;
      remoteHurtSeq = source?.hurt.seq ?? 0;
      bust = 0;
      stillFor = 0;
      resetArmed();
      // The local force stands down: the server's units take over (and hand back on going offline).
      for (const c of cops.slice()) removeCop(c);
      for (const u of cars.slice()) despawnCar(u);
      incident = null;
    },
    get remote() {
      return remote !== null;
    },
    get health() {
      return health;
    },
    reportIncident(pos, type, severity = 1) {
      if (remote) return; // online the server's units respond
      const now = ctx.state.time;
      if (level() > 0 || now < incidentReadyAt) return;
      if (type === 'vehicleDamage' && severity < 2 && rng.next() < 0.6) return; // most bumps aren't worth a look
      const patrol = cars
        .filter((u) => u.role === 'patrol' && !u.disabled)
        .sort((a, b) => dist2(a.vehicle.pos, pos) - dist2(b.vehicle.pos, pos))[0];
      if (!patrol || dist2(patrol.vehicle.pos, pos) > 240 * 240) return;
      incident = { pos: { x: pos.x, z: pos.z }, until: now + 40 };
      incidentReadyAt = now + 25;
      setRole(patrol, 'respond');
    },
    strikeOfficer(unitId, velocity, lethal, byPlayer = true) {
      if (remote) return false;
      const c = cops.find((o) => o.id === unitId && o.state !== 'down');
      if (!c) return false;
      // Officers who go down stay down until they're cleared away, so `lethal` changes nothing here yet.
      void lethal;
      knockCop(c, byPlayer, velocity);
      return true;
    },
    get armedAuthorised() {
      return remote ? remote.armedStage >= 1 : armedAuthorised;
    },
    standBack(pos, radius) {
      if (remote) return 0;
      let n = 0;
      for (const u of cars.slice()) {
        if (u.disabled || u.role === 'pursue') continue;
        const near = Math.hypot(u.vehicle.pos.x - pos.x, u.vehicle.pos.z - pos.z) <= radius;
        const seen = view.isVisible(u.vehicle.pos.x, u.vehicle.pos.z, 6);
        if (!near && !seen) continue;
        if (seen) {
          setRole(u, 'leave');
          u.standBackUntil = ctx.state.time + STAND_BACK_S;
        } else despawnCar(u);
        n++;
      }
      for (const c of cops.slice()) {
        if (c.state === 'down' || Math.hypot(c.body.pos.x - pos.x, c.body.pos.z - pos.z) > radius) continue;
        removeCop(c);
        n++;
      }
      return n;
    },
    spawnPatrol(pos) {
      if (!net || remote) return -1;
      const hit = net.nearestLane(pos);
      if (!hit) return -1;
      let u = spawnPatrolAt(hit.lane, hit.s);
      if (!u) {
        // Slot taken by traffic: place it anyway where the ground is clear (there, or a car length or two
        // either way along the lane), then let traffic drive it.
        const lane = hit.lane;
        const heading = Math.atan2(lane.dir.x, lane.dir.z);
        let p: V2 | null = null;
        for (const ds of [0, -6, 6, -12, 12]) {
          const s = hit.s + ds;
          if (s < 0 || s > lane.length) continue;
          const q = { x: lane.a.x + lane.dir.x * s, z: lane.a.z + lane.dir.z * s };
          if (ctx.physics.isShapeFree({ type: 'box', hx: 0.95, hz: 2.4 }, q, heading, Layer.VEHICLE | Layer.STATIC | Layer.PLAYER)) {
            p = q;
            break;
          }
        }
        if (!p) return -1;
        const v = vehicles.spawn('police', p, { heading, controller: 'ai', occupant: { kind: 'ped', pedId: null }, tags: ['police'] });
        u = addCar(v, 'patrol');
        if (!traffic?.adopt(v, true)) u.role = 'leave';
      }
      return u.vehicle.id;
    },
  };

  /**
   * Online step: no local units, no local arrests. Health still regenerates (and a fatal local hurt —
   * traffic, a fall — still runs the WASTED sequence, which the server confirms); police gunfire is
   * the server's, so its health reports are adopted without ending the player here (the server's own
   * `wasted` follows when it is fatal).
   */
  function remoteStep(dt: number): void {
    const src = remote!;
    if (src.hurt.seq !== remoteHurtSeq) {
      remoteHurtSeq = src.hurt.seq;
      health = Math.max(1, Math.min(HEALTH_MAX, src.hurt.health));
      lastHurtAt = ctx.state.time;
    }
    armedAuthorised = src.armedStage >= 1;
    armedCleared = src.armedStage >= 2;
    if (outcome !== 'none') {
      outcomeTimer -= dt;
      if (outcomeTimer <= 0 && (!outcomeGate || outcomeGate(outcome))) finishOutcome();
    } else updateHealth(dt);
    rebuildObservers();
  }

  function remoteUnitInfo(u: RemotePoliceUnit): PoliceUnitInfo {
    return {
      id: u.id,
      kind: u.kind,
      role: u.down ? 'disabled' : u.kind === 'foot' ? 'pursue' : u.siren ? 'pursue' : 'patrol',
      x: +u.x.toFixed(2),
      z: +u.z.toFixed(2),
      speed: +u.speed.toFixed(2),
      vehicleId: u.vehicleId,
      siren: u.siren,
      health: u.health,
      seesPlayer: seers.has(u.id),
      tactic: '',
      armed: u.armed,
      aiming: u.aiming ? 1 : 0,
      crewOut: false,
      ...(u.aimAt ? { aimAt: u.aimAt } : {}),
    };
  }

  function rebuildObservers(): void {
    observers.length = 0;
    const lv = level();
    if (remote) {
      for (const u of remote.units()) {
        if (u.down) continue;
        observers.push({ x: u.x, z: u.z, range: u.kind === 'car' ? CAR_SIGHT[lv]! : FOOT_SIGHT, unitId: u.id });
      }
      return;
    }
    for (const u of cars) {
      if (u.disabled || u.crewInCar <= 0) continue;
      const range = u.role === 'roadblock' ? ROADBLOCK_SIGHT : CAR_SIGHT[lv]!;
      observers.push({ x: u.vehicle.pos.x, z: u.vehicle.pos.z, range, unitId: u.id });
    }
    for (const c of cops) {
      if (c.state === 'down') continue;
      observers.push({ x: c.body.pos.x, z: c.body.pos.z, range: FOOT_SIGHT, unitId: c.id });
    }
  }

  return {
    id: 'police',
    order: 250,
    init(c) {
      ctx = c;
      rng = c.rng.fork('police');
      layout = c.services.get('layout');
      view = new ViewTester(c, () => playerPos());
      c.services.provide('police', service);
      copView = new CopView(c.scene);
      if (c.render) stingerView = new StingerView(c.scene);
      // The strips on the ground follow the events, offline (placed here) and online (the server's, via the net module).
      offs.push(
        c.events.on('stingerPlaced', (e) => stingerView?.add(e.id, e.a, e.b)),
        c.events.on('stingerRemoved', (e) => stingerView?.remove(e.id)),
      );
      c.hooks.register('detailedCops', () => copView?.detailedCount() ?? 0);
      // Constables on foot are the detailed model (docs/POLISH.md "Detailed figures"); `?police=classic` is a
      // developer switch back to the instanced figure.
      const classicCops = typeof location !== 'undefined' && new URLSearchParams(location.search).get('police') === 'classic';
      if (c.render && !classicCops) copView.setDetailed(true);

      offs.push(
        c.events.on('collision', (e) => {
          const b = e.b;
          if (!b) return;
          // Foot officer hit by a vehicle.
          for (const [me, other] of [
            [e.a, b],
            [b, e.a],
          ] as const) {
            if (me.kind === 'ped' && me.id >= COP_ENTITY_BASE && other.kind === 'vehicle' && e.speed > 4) {
              const cop = copFor(me.id);
              const veh = vehicles.get(other.id);
              // The car has to be moving: an officer sprinting into a parked car just bounces off.
              if (cop && veh && veh.speed > 4) {
                // Knocked down, but whose crime? Only the driver's when the car moved INTO him (as online,
                // docs/POLICE.md §10): an officer stepping into the flank of a car pulling away from his
                // door is down, not assaulted (hitPolice is severity 4: level 1 to 3 at a stroke).
                const dx = cop.body.pos.x - veh.pos.x;
                const dz = cop.body.pos.z - veh.pos.z;
                const dl = Math.hypot(dx, dz) || 1;
                const into = (veh.velocity.x * dx + veh.velocity.z * dz) / dl;
                knockCop(cop, veh.controller === 'player' && into >= OFFICER_HIT_INTO, veh.velocity);
              }
            }
          }
          // Player's car rams a police car (online the server judges rams against its own units).
          if (e.a.kind === 'vehicle' && b.kind === 'vehicle' && !remote) {
            const va = vehicles.get(e.a.id);
            const vb = vehicles.get(b.id);
            if (!va || !vb) return;
            const pv = va.controller === 'player' ? va : vb.controller === 'player' ? vb : null;
            const cv = pv === va ? vb : pv === vb ? va : null;
            if (!pv || !cv || !cv.isPolice || pv.destroyed) return;
            const unit = cars.find((u) => u.vehicle === cv);
            const now = ctx.state.time;
            const dv = e.impulse / cv.spec.mass;
            // Who drove into whom? Compare closing speeds along the contact normal.
            const nx = pv === va ? e.normal.x : -e.normal.x; // from the player's car towards the police car
            const nz = pv === va ? e.normal.z : -e.normal.z;
            const playerInto = pv.velocity.x * nx + pv.velocity.z * nz;
            const copInto = -(cv.velocity.x * nx + cv.velocity.z * nz);
            // Whose ram? Yours when you supplied the lion's share of the closing speed: running into a
            // stopped, parked or slower unit, or ploughing into one coming the other way at a crawl, is
            // hitPolice; a head-on where the unit was closing at least half as fast as you were is a
            // crash between two cars going for the same gap, nobody's crime (being rammed by a unit never
            // is, as online; docs/POLICE.md §12). Online charges any head-on you were quicker in.
            const playerFault = playerInto > 3 && playerInto > copInto * UNIT_RAM_SHARE;
            // Under POLICE_TAP_SPEED of closing it's a tap (severity 1, a minor offence: a warning the first
            // time when you weren't wanted), above it a ram (docs/POLICE.md §1). Online: server/police/force.ts.
            const severity = playerInto < POLICE_TAP_SPEED ? 1 : 3;
            // A proper ram brings the armed cars a level early (docs/POLICE-AI-POLISH.md).
            if (playerFault && playerInto >= HARD_RAM_SPEED && level() > 0) hardRam = true;
            if (unit) {
              // Whose damage this contact does (forced crashes, docs/POLICE.md §14): a ram of yours never
              // lays a unit on the tarmac, the unit running into you does count as its own crash.
              if (playerFault) unit.blameAt = now;
              else unit.excusedAt = now;
              // Back off a moment after contact so a ram doesn't become a permanent shove.
              if (unit.tactic === 'ram' || unit.tactic === 'box') unit.backoffUntil = now + 0.6;
              if (dv > 2 && playerFault && now >= unit.hitReadyAt) {
                unit.hitReadyAt = now + 2.5;
                ctx.events.emit('crime', { type: 'hitPolice', pos: { x: e.pos.x, z: e.pos.z }, severity, byPlayer: true, vehicleId: pv.id });
              }
            } else if (dv > 2 && playerFault) {
              ctx.events.emit('crime', { type: 'hitPolice', pos: { x: e.pos.x, z: e.pos.z }, severity, byPlayer: true, vehicleId: pv.id });
            }
          }
        }),
        c.events.on('crime', (e) => {
          if (e.byPlayer && FIREARM_CRIMES.has(e.type)) firearmsIncident = true;
        }),
        c.events.on('heatCleared', () => resetArmed()),
        c.events.on('playerHurt', (e) => {
          if (outcome !== 'none') return;
          health = Math.max(0, health - e.amount);
          lastHurtAt = ctx.state.time;
          if (health <= 0) beginOutcome('wasted', e.cause);
        }),
      );

      c.hooks.register('getPolice', () => service.info());
      c.hooks.register('spawnPolice', (x, z) => service.spawnPatrol({ x, z }));
      c.hooks.register('setPlayerHealth', (hp) => {
        health = clamp(hp, 0, HEALTH_MAX);
        lastHurtAt = ctx.state.time;
        if (health <= 0) beginOutcome('wasted', 'debug');
      });
      c.hooks.registerSnapshot('police', () => {
        const i = service.info();
        return { ...i, units: i.units.slice(0, 16) };
      });
    },
    start() {
      vehicles = ctx.services.get('vehicles');
      traffic = ctx.services.tryGet('traffic');
      net = traffic?.roadNet;
      const roads = layout.roads;
      const onRoad = new Set<number>();
      for (const e of roads.edges) {
        if (e.alley) continue;
        onRoad.add(e.from);
        onRoad.add(e.to);
      }
      searchNodes = roads.nodes.filter((n) => onRoad.has(n.id));
      // A caller has just put the suspect somewhere else: searchers drop what they were doing and go there.
      offs.push(
        ctx.events.on('sightingReported', () => {
          for (const u of cars) if (u.role === 'search') u.goal = null;
        }),
      );
    },
    fixedUpdate(dt) {
      const now = ctx.state.time;
      if (remote) {
        remoteStep(dt);
        return;
      }
      if (incident && now > incident.until) incident = null;
      maintainUnits(dt);
      if (outcome !== 'none') {
        outcomeTimer -= dt;
        for (const u of cars) {
          if (u.role === 'patrol' || u.disabled) continue;
          u.vehicle.controls.throttle = 0;
          u.vehicle.controls.handbrake = true;
        }
        for (const c2 of cops) c2.body.setVelocity({ x: 0, z: 0 }, 0);
        if (outcomeTimer <= 0 && (!outcomeGate || outcomeGate(outcome))) finishOutcome();
        rebuildObservers();
        return;
      }
      composeForce();
      updateArmedResponse();
      assignTactics();
      const sp = target().speed;
      if (sp < 1) stillFor += dt;
      else if (sp > 2.5) stillFor = 0;
      deployFootOfficers();
      for (const u of cars) {
        driveUnit(u, dt);
        carGun(u, dt);
      }
      updateStingers();
      updateCops(dt);
      updateBust(dt);
      updateHealth(dt);
      rebuildObservers();
    },
    update(_dt, alpha) {
      if (!copView) return;
      const now = ctx.state.time;
      poses.length = 0;
      if (remote) {
        // The server's officers, at the net module's render time (already interpolated).
        for (const u of remote.units(alpha)) {
          if (u.kind !== 'foot') continue;
          poses.push({ x: u.x, z: u.z, prevX: u.x, prevZ: u.z, yaw: u.h, phase: 0, speed: u.speed, lean: u.down ? -Math.PI / 2 + 0.1 : 0, fade: 1, running: u.running, armed: u.armed, aim: u.aiming ? 1 : 0, firedAgo: u.firedAgo });
        }
        copView.update(poses, alpha, ctx.state.time + alpha * FIXED_DT);
        return;
      }
      for (const c of cops) {
        poses.push({
          x: c.body.pos.x,
          z: c.body.pos.z,
          prevX: c.prevX,
          prevZ: c.prevZ,
          yaw: c.yaw,
          phase: c.phase,
          speed: c.speed,
          lean: c.lean,
          fade: c.fade,
          running: c.speed > 3.5,
          armed: c.armed,
          aim: c.aim > 0 ? 1 : 0,
          firedAgo: now - c.firedAt,
        });
      }
      copView.update(poses, alpha, ctx.state.time + alpha * FIXED_DT);
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
      for (const c of cops.slice()) removeCop(c);
      for (const u of cars.slice()) despawnCar(u);
      copView?.dispose();
      copView = null;
      stingerView?.dispose();
      stingerView = null;
    },
  };
}

/** Where a crew that lost sight of the suspect at `u.lastSawAt` thinks they are now (src/police/sighting.ts). */
function crewEstimate(u: CarUnit, t: { onFoot: boolean; vehicle: Vehicle | null }, now: number): SuspectEstimate & { onFoot: boolean; vehicle: Vehicle | null } {
  return { ...deadReckon({ pos: u.sawPos, vel: u.sawVel, heading: u.sawHeading, at: u.lastSawAt }, now), onFoot: t.onFoot, vehicle: t.vehicle };
}

function dist2(a: V2, b: V2): number {
  return (a.x - b.x) ** 2 + (a.z - b.z) ** 2;
}

